/* @flow */

import * as React from 'react';
import classNames from 'classnames';
import useSheet from 'app/jss';
import colorUtils from 'color';
import glamorous from 'glamorous';
import { FRIEND, YOU_REQUESTED, THEY_REQUESTED, NOT_FRIEND } from 'app/constants/friendStatusTypes';
import Avatar from 'app/components/commons/Avatar';
import Tappable from 'app/components/commons/Tappable';
import FriendRowButton from './FriendRowButton';

type Props = {
  classes: Object,
  friend: Friend | FriendRequest,
  group?: FriendGroup,
  isCreatingFriendRequest?: boolean,
  isFriendsGroup?: boolean,
  isSaving?: boolean,
  isSavingFriendRequest?: boolean,
  members?: Array<number | string>,
  onAddToGroup: (groupId: string | number, friendId: number) => void,
  onCreateFriendRequest: () => void,
  onRemoveFriend: (friendId: number) => void,
  onRowClick: (user: iFriendUser) => void,
};

type State = {
  addedToGroup: boolean,
  asyncMode: boolean,
  friendRequestSent: boolean,
  removedFriend: boolean,
};

const StyledAvatar = glamorous(Avatar)({
  fontSize: '0.7rem', // icon status size
});

class FriendRow extends React.Component<Props, State> {
  props: Props;
  state: State = {
    addedToGroup: false,
    asyncMode: false,
    friendRequestSent: false,
    removedFriend: false,
  };
  timerId: number; // eslint-disable-line react/sort-comp

  static defaultProps = {
    group: undefined,
    isCreatingFriendRequest: false,
    isFriendsGroup: false,
    isSaving: false,
    isSavingFriendRequest: false,
    members: [],
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.state.asyncMode) {
      const { isSavingFriendRequest, members, friend } = this.props;

      const nextMembers = nextProps.members;
      const addedToGroup = !members.includes(friend.id) && nextMembers.includes(friend.id);
      const removedFriend = members.includes(friend.id) && !nextMembers.includes(friend.id);
      const friendRequestSent = isSavingFriendRequest && !nextProps.isSavingFriendRequest;
      const asyncMode = !addedToGroup && !friendRequestSent && !removedFriend;

      this.setState({
        addedToGroup,
        asyncMode,
        friendRequestSent,
        removedFriend,
      });

      this.timerId = window.setTimeout(() => {
        this.setState({
          addedToGroup: false,
          friendRequestSent: false,
          removedFriend: false,
        });
      }, 3000);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timerId);
  }

  handleAddToGroup = (event: SyntheticEvent<*>) => {
    const { friend, group, onAddToGroup, onCreateFriendRequest } = this.props;
    const groupId = group ? group.id : undefined;

    event.stopPropagation();

    if (friend.status === NOT_FRIEND) {
      onCreateFriendRequest([friend.id], groupId);
    } else {
      this.setState(
        {
          asyncMode: true,
        },
        () => {
          onAddToGroup([friend.id], groupId);
        },
      );
    }
  };

  handleCreateFriendRequest = (event: SyntheticEvent<*>) => {
    const { friend, group, onCreateFriendRequest } = this.props;
    const groupId = group ? group.id : undefined;
    event.stopPropagation();

    this.setState(
      {
        asyncMode: true,
      },
      () => {
        onCreateFriendRequest([friend.id], groupId);
      },
    );
  };

  handleRemoveFriend = (event: SyntheticEvent<*>) => {
    const { friend, onRemoveFriend } = this.props;

    event.stopPropagation();

    this.setState(
      {
        asyncMode: true,
      },
      () => {
        onRemoveFriend(friend.id);
      },
    );
  };

  handleRowClick = () => {
    const { friend, onRowClick } = this.props;
    onRowClick(friend);
  };

  render() {
    const {
      classes,
      friend: { avatar, id: friendId, status, username },
      isCreatingFriendRequest,
      isFriendsGroup,
      isSaving,
      members,
    } = this.props;
    const { addedToGroup, friendRequestSent, removedFriend } = this.state;

    return (
      <Tappable
        component="div"
        className={classNames(classes.friendRow, {
          friends: status === FRIEND,
          'they-requested': status === THEY_REQUESTED,
          'you-requested': status === YOU_REQUESTED,
          'not-friend': status === NOT_FRIEND,
        })}
        onTap={this.handleRowClick}
      >
        <div className={classes.row}>
          <StyledAvatar avatar={avatar} size="xsmall" status={status} />
          <span className={classes.name}> {username} </span>
          <FriendRowButton
            addedToGroup={addedToGroup}
            friendRequestSent={friendRequestSent}
            friendStatus={status}
            hasFriend={members.includes(friendId)}
            isCreatingFriendRequest={isCreatingFriendRequest}
            isFriendsGroup={isFriendsGroup}
            isSaving={isSaving}
            onAddToGroup={this.handleAddToGroup}
            onCreateFriendRequest={this.handleCreateFriendRequest}
            onRemoveFriend={this.handleRemoveFriend}
            removedFriend={removedFriend}
          />
        </div>
      </Tappable>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    friendRow: {
      borderBottom: {
        color: colorUtils(theme.color.basis4)
          .lighten(0.26)
          .hex(),
        style: 'solid',
        width: 1,
      },
      fontSize: 16,
      overflow: 'hidden',
      position: 'relative',
      transform: 'translateZ(0px)',
      '&.friends $row': {
        backgroundColor: theme.color.white,
      },
      [theme.mediaQuery.tabletPortraitAndUp]: {
        fontSize: 'inherit',
      },
    },
    name: {
      color: theme.color.basis2,
      flexGrow: 1,
    },
    row: {
      alignItems: 'center',
      backgroundColor: colorUtils(theme.color.basis6)
        .lighten(0.045)
        .hex(),
      display: 'flex',
      height: 60,
      overflow: 'hidden',
      paddingLeft: 10,
      // safari has problems with z-index and positioned elements apparently
      // row message was appearing above 'row', this seems to fix it
      transform: 'translateZ(0)',
      transition: 'width 0.3s ease-out',
      zIndex: 1,
      [theme.mediaQuery.tabletPortraitAndUp]: {
        height: 50,
      },
      '& > *:not(:last-child)': {
        marginRight: 10,
      },
    },
    rowMessage: {
      alignItems: 'center',
      display: 'flex',
      width: '100%',
    },
  };
}

export default useSheet(applyStyles)(FriendRow);
